import { useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { AddressAutocomplete } from "./AddressAutocomplete";
import { CalendarCheck, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";

const PROFESSIONI = ["Privato", "Azienda / P.IVA", "Agricoltore", "Installatore", "Altro"];
const ignorePac = (e) => { if (e.detail?.originalEvent?.target?.closest?.(".pac-container")) e.preventDefault(); };

export default function FreeDownloadGate({ selfToken, disabled, label = "Scarica il PDF della consulenza" }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ nome: "", cognome: "", professione: "", email: "", phone: "", address: "" });
  const [busy, setBusy] = useState(false);
  const reteMode = new URLSearchParams(window.location.search).get("rete") === "1";
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const finalizeRete = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/public/free/${selfToken}/finalize-rete`);
      window.location.assign(`/c/${data.token}?rete=1`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Errore, riprova");
      setBusy(false);
    }
  };

  const recupera = async (checked) => {
    if (!checked) return;
    try {
      const { data } = await api.get(`/public/free/${selfToken}/prefill`);
      setF((s) => ({
        nome: data.nome || s.nome, cognome: data.cognome || s.cognome,
        professione: data.professione || s.professione, email: data.email || s.email,
        phone: data.phone || s.phone, address: data.address || s.address,
      }));
      toast.success("Dati recuperati");
    } catch {
      toast.error("Nessun dato precedente da recuperare");
    }
  };

  const submit = async () => {
    if (!f.nome.trim() || !f.cognome.trim()) return toast.error("Inserisci nome e cognome");
    if (!f.professione) return toast.error("Seleziona la professione");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim())) return toast.error("Email non valida");
    if (f.phone.replace(/\D/g, "").length < 8) return toast.error("Inserisci un cellulare valido");
    if (!f.address.trim()) return toast.error("Inserisci l'indirizzo");
    setBusy(true);
    try {
      const { data } = await api.post(`/public/free/${selfToken}/register`, {
        nome: f.nome.trim(), cognome: f.cognome.trim(), professione: f.professione,
        email: f.email.trim(), phone: f.phone.trim(), address: f.address.trim(),
        origin_url: window.location.origin,
      });
      toast.success("Ti abbiamo inviato la consulenza via email. La apriamo subito qui.");
      window.location.assign(`/c/${data.token}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Errore, riprova");
      setBusy(false);
    }
  };

  return (
    <>
      <Button onClick={() => (reteMode ? finalizeRete() : setOpen(true))} disabled={disabled || busy} data-testid="free-download-btn" size="lg" className="gap-2">
        {busy && reteMode ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarCheck className="h-4 w-4" />} {reteMode ? "Genera consulenza" : label}
      </Button>
      <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <DialogContent className="max-w-md" data-testid="free-register-dialog" onPointerDownOutside={ignorePac} onInteractOutside={ignorePac}>
          <DialogHeader><DialogTitle>Ricevi la tua consulenza gratuita</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">Ti inviamo l'analisi completa via email e la apri subito online, con la possibilità di prenotare un consulente.</p>

          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
            <Checkbox onCheckedChange={recupera} data-testid="free-recupera" /> Recupera i dati già inseriti
          </label>

          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Nome *</Label><Input value={f.nome} onChange={set("nome")} data-testid="free-nome" /></div>
            <div><Label className="text-xs">Cognome *</Label><Input value={f.cognome} onChange={set("cognome")} data-testid="free-cognome" /></div>
          </div>
          <div>
            <Label className="text-xs">Professione *</Label>
            <Select value={f.professione} onValueChange={(v) => setF((s) => ({ ...s, professione: v }))}>
              <SelectTrigger data-testid="free-professione"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
              <SelectContent>
                {PROFESSIONI.map((p) => <SelectItem key={p} value={p} data-testid={`free-prof-${p}`}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Email *</Label><Input type="email" value={f.email} onChange={set("email")} data-testid="free-email" /></div>
            <div><Label className="text-xs">Cellulare *</Label><Input type="tel" value={f.phone} onChange={set("phone")} data-testid="free-phone" /></div>
          </div>
          <div>
            <Label className="text-xs">Indirizzo *</Label>
            <AddressAutocomplete value={f.address} onChange={(v) => setF((s) => ({ ...s, address: v }))} onSelect={(addr) => setF((s) => ({ ...s, address: addr }))} placeholder="Via, civico, città…" data-testid="free-address" />
          </div>

          <Button onClick={submit} disabled={busy} data-testid="free-register-submit" className="w-full mt-1 gap-2">
            {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Invio in corso…</> : <><Mail className="h-4 w-4" /> Invia e visualizza la consulenza</>}
          </Button>
          <p className="text-[11px] text-muted-foreground text-center">Il link della consulenza è valido 20 giorni (non prorogabile). Non condivideremo i tuoi dati con terzi.</p>
        </DialogContent>
      </Dialog>
    </>
  );
}
